import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/********************************************************************************
 * Copyright (c) 2020 TypeFox and others
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License v. 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 ********************************************************************************/
import { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { Box } from '@mui/material';
import { isError } from '../../extension-registry-types';
import { MainContext } from '../../context';
import { DelayedLoadIndicator } from '../delayed-load-indicator';
import { ManageExtensionCard } from '../extension/manage-extension-card';
import { ExtensionGrid } from '../page-primitives';
import { EmptyPlaceholder, SettingsSectionTitle } from '../../pages/user/settings/settings-primitives';
export const NamespaceExtensionList = props => {
    const [extensions, setExtensions] = useState();
    const [loading, setLoading] = useState(true);
    const context = useContext(MainContext);
    // The card links back to wherever the namespace is being viewed, user settings or admin alike.
    const { pathname } = useLocation();
    const fetchExtension = props.fetchExtension ??
        ((abortController, extension) => context.service.getExtensionDetail(abortController, extension.url));
    // A single effect keyed on the namespace name owns the fetch lifecycle: it resets state, loads the
    // extensions, and creates a fresh AbortController each run so that switching namespaces (or unmounting)
    // aborts the previous namespace's in-flight requests instead of letting a stale response overwrite the
    // current one. (Aborts are ignored by the error handler, so this never surfaces a spurious error.)
    useEffect(() => {
        const abortController = new AbortController();
        setExtensions(undefined);
        setLoading(true);
        updateExtensions(abortController);
        return () => abortController.abort();
    }, [props.namespace.name]);
    const updateExtensions = async (abortController) => {
        const entries = Object.keys(props.namespace.extensions).map((name) => ({
            name,
            url: props.namespace.extensions[name]
        }));
        const getExtension = async (entry) => {
            try {
                const result = await fetchExtension(abortController, entry);
                if (isError(result)) {
                    throw result;
                }
                return result;
            }
            catch (error) {
                // Only a 404 is the expected absence omitUnavailable is about. A network failure or
                // a 5xx still has to be reported, or the list would quietly look complete.
                const notFound = error?.status === 404;
                if (!props.omitUnavailable || !notFound) {
                    context.handleError(error);
                }
                return undefined;
            }
        };
        const extensionUnfiltered = await Promise.all(entries.map(getExtension));
        const extensions = extensionUnfiltered.filter(e => e != null);
        setExtensions(extensions);
        setLoading(false);
    };
    return (_jsxs(Box, { children: [_jsx(SettingsSectionTitle, { component: 'h3', children: "Extensions" }), _jsx(DelayedLoadIndicator, { loading: loading }), extensions && extensions.length > 0 ? (_jsx(ExtensionGrid, { children: extensions.map(extension => (_jsx(ManageExtensionCard, { extension: extension, routePrefix: props.routePrefix, linkState: {
                        backTo: pathname,
                        backLabel: `Back to ${props.namespace.name}`
                    } }, `${extension.namespace}.${extension.name}-${extension.version}`))) })) : !loading ? (_jsx(EmptyPlaceholder, { children: "No extensions published under this namespace yet." })) : null] }));
};
//# sourceMappingURL=namespace-extension-list.js.map